// src/pages/customer-panel/payment-methods/PaymentMethodsPage.js
// Payment Method Management — Stripe Elements + SetupIntent flow

import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: '16px',
            color: '#1e293b',
            '::placeholder': { color: '#94a3b8' },
        },
        invalid: { color: '#ef4444', iconColor: '#ef4444' },
    },
};

// --- Stripe Add Card Form (must be inside <Elements>) ---
function AddCardForm({ clientSecret, onSuccess, onClose }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);
        setError(null);

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setError('Card element not found.');
            setProcessing(false);
            return;
        }

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement },
        });

        if (setupError) {
            setError(setupError.message || 'An error occurred.');
            setProcessing(false);
            return;
        }

        if (setupIntent?.status === 'succeeded') {
            const paymentMethodId = setupIntent.payment_method;
            try {
                await axiosInstance.post('/payment-methods', {
                    gateway_payment_method_id: paymentMethodId,
                });
                onSuccess('Payment method added successfully!');
                onClose();
            } catch (err) {
                setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save payment method.');
            }
        } else {
            setError(`Card setup failed with status: ${setupIntent?.status}`);
        }
        setProcessing(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                Your card details are sent directly to Stripe and never touch our servers.
            </Typography>
            <Box
                sx={{
                    p: 2, border: '1px solid #e2e8f0', borderRadius: '12px',
                    bgcolor: '#fff', mb: 2,
                }}
            >
                <CardElement options={CARD_ELEMENT_OPTIONS} />
            </Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.85rem' }}>
                    {error}
                </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 1 }}>
                <Button
                    onClick={onClose}
                    sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!stripe || processing}
                    startIcon={processing ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{
                        fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                        textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                    }}
                >
                    {processing ? 'Saving...' : 'Save Card'}
                </Button>
            </Box>
        </form>
    );
}

// --- Main Page Component ---
export default function PaymentMethodsPage() {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [creatingIntent, setCreatingIntent] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchPaymentMethods = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/payment-methods');
            const data = response.data?.payment_methods || response.data || [];
            setMethods(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch payment methods:', err);
            setError(err.response?.data?.message || 'Failed to load payment methods.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Payment Methods | Customer Panel';
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    const handleOpenAddDialog = async () => {
        setCreatingIntent(true);
        try {
            const response = await axiosInstance.post('/payment-methods/setup-intent');
            const secret = response.data?.client_secret;
            if (secret) {
                setClientSecret(secret);
                setDialogOpen(true);
            } else {
                setSnackbar({ open: true, message: 'Could not prepare the payment form.', severity: 'error' });
            }
        } catch (err) {
            console.error('Failed to create setup intent:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Could not prepare the payment form.',
                severity: 'error',
            });
        } finally {
            setCreatingIntent(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setClientSecret(null);
    };

    const handleSetDefault = async (method) => {
        try {
            await axiosInstance.post(`/payment-methods/${method.id}/default`);
            setSnackbar({ open: true, message: 'Default payment method updated!', severity: 'success' });
            fetchPaymentMethods();
        } catch (err) {
            console.error('Failed to set default:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to set default payment method.',
                severity: 'error',
            });
        }
    };

    const handleDelete = async (method) => {
        if (!window.confirm('Are you sure you want to remove this payment method?')) return;
        try {
            await axiosInstance.delete(`/payment-methods/${method.id}`);
            setSnackbar({ open: true, message: 'Payment method removed!', severity: 'success' });
            fetchPaymentMethods();
        } catch (err) {
            console.error('Failed to delete payment method:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Failed to remove payment method.',
                severity: 'error',
            });
        }
    };

    const handleAddSuccess = (message) => {
        setSnackbar({ open: true, message, severity: 'success' });
        fetchPaymentMethods();
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2, px: 3, mb: 3, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PaymentIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Payment Methods</Typography>
                            <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>Manage your saved payment methods</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchPaymentMethods} sx={{ color: '#fff' }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            startIcon={creatingIntent ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                            variant="contained"
                            onClick={handleOpenAddDialog}
                            disabled={creatingIntent}
                            sx={{
                                fontFamily, fontWeight: 600, bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px', textTransform: 'none', px: 3,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            {creatingIntent ? 'Preparing...' : 'Add Card'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>{error}</Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} sx={{ color: GREEN_ACCENT }} />
                </Box>
            ) : methods.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '16px', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CreditCardIcon sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                        No Payment Methods
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                        Add a payment method to get started
                    </Typography>
                    <Button
                        startIcon={creatingIntent ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                        variant="contained"
                        onClick={handleOpenAddDialog}
                        disabled={creatingIntent}
                        sx={{
                            fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px',
                            textTransform: 'none', '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        {creatingIntent ? 'Preparing...' : 'Add Payment Method'}
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {methods.map((method) => {
                        const isDefault = method.is_default === true || method.is_default === 1;
                        const brand = method.card_brand || 'card';
                        const last4 = method.last4 || '****';
                        const expiry = method.exp_month && method.exp_year
                            ? `${String(method.exp_month).padStart(2, '0')}/${method.exp_year}`
                            : '--';

                        return (
                            <Grid item xs={12} sm={6} md={4} key={method.id}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: isDefault ? `2px solid ${GREEN_ACCENT}` : '1px solid',
                                        borderColor: isDefault ? GREEN_ACCENT : 'grey.100',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                                        },
                                        position: 'relative',
                                    }}
                                >
                                    {isDefault && (
                                        <Chip
                                            label="Default"
                                            size="small"
                                            sx={{
                                                position: 'absolute', top: 12, right: 12,
                                                fontFamily, fontWeight: 600, fontSize: '0.7rem',
                                                bgcolor: `${GREEN_ACCENT}15`, color: GREEN_ACCENT,
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ mb: 2 }}>
                                            <CreditCardIcon sx={{ fontSize: 32, color: GREEN_ACCENT }} />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily, fontWeight: 700, fontSize: '1.2rem',
                                                color: '#1e293b', letterSpacing: '2px', mb: 1,
                                            }}
                                        >
                                            •••• •••• •••• {last4}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                    Brand
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: '#1e293b', textTransform: 'capitalize' }}>
                                                    {brand}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                                                    Expires
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontFamily, fontWeight: 600, color: '#1e293b' }}>
                                                    {expiry}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {!isDefault && (
                                                    <Tooltip title="Set as default">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleSetDefault(method)}
                                                            sx={{ color: '#f59e0b' }}
                                                        >
                                                            <StarOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Remove">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(method)}
                                                        sx={{ color: '#ef4444' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Add Card Dialog with Stripe Elements */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    Add Payment Method
                    <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {clientSecret && (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <AddCardForm
                                clientSecret={clientSecret}
                                onSuccess={handleAddSuccess}
                                onClose={handleCloseDialog}
                            />
                        </Elements>
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
